import { CHART } from '@/lib/chartColors';
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AreaChart, Area, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { BarChart3 } from 'lucide-react';
import { useUsageSummary, useAccountBalance } from '@/hooks/useApi';
import { useEnvironment } from '@/contexts/EnvironmentContext';
import { formatCompact, formatNumber } from '@/lib/utils';
import { PageHeader } from '@/components/ui/PageHeader';
import { Panel } from '@/components/ui/Panel';
import { Segmented } from '@/components/ui/Segmented';
import { StatTile } from '@/components/ui/StatTile';
import { EmptyState } from '@/components/ui/EmptyState';
import { Table, THead, TBody, Tr, Th, Td, TableSkeleton } from '@/components/ui/Table';
import { ChartTooltip } from '@/components/charts/ChartTooltip';

type Period = '7d' | '30d' | '90d';

const PERIOD_DAYS: Record<Period, number> = { '7d': 7, '30d': 30, '90d': 90 };
const PERIODS: { value: Period; label: string }[] = [
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
  { value: '90d', label: '90 days' },
];
const shortDate = (iso: unknown) => typeof iso === 'string' && /^\d{4}-\d{2}-\d{2}/.test(iso)
  ? new Date(`${iso.slice(0, 10)}T00:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' })
  : String(iso);

function formatMs(ms: number | null): string {
  return ms === null ? 'None yet' : `${Math.round(ms)}ms`;
}

const UsagePage: React.FC = () => {
  const [period, setPeriod] = useState<Period>('30d');
  const { environment } = useEnvironment();

  const { start, end } = useMemo(() => {
    const now = Math.floor(Date.now() / 1000);
    return { start: now - PERIOD_DAYS[period] * 86400, end: now };
  }, [period]);

  const { data: usage, isLoading: usageLoading } = useUsageSummary({ start, end, granularity: 'day', environment });
  const { data: balance, isLoading: balanceLoading } = useAccountBalance();

  const serviceRows = Object.entries(usage?.by_service ?? {})
    .map(([service, stat]) => ({ service, ...stat }))
    .sort((a, b) => b.requests - a.requests);
  const byDay = usage?.by_day ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Usage & Analytics"
        description="Requests, errors and response times for your keys."
        actions={<Segmented label="Period" value={period} options={PERIODS} onChange={setPeriod} />}
      />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
        <StatTile
          label="Requests"
          value={usage ? formatNumber(usage.total_requests) : '0'}
          hint={`Last ${PERIOD_DAYS[period]} days`}
          trend={byDay.map((d) => d.requests)}
          loading={usageLoading}
        />
        <StatTile label="Error rate" value={usage ? `${usage.error_rate.toFixed(1)}%` : '0%'} hint="Failed calls cost nothing" loading={usageLoading} />
        <StatTile label="Median latency" value={usage ? formatMs(usage.median_response_time_ms) : 'None yet'} hint="Time to answer, at the gateway" loading={usageLoading} />
      </div>

      <Panel title="Requests per day">
        {usageLoading ? (
          <div className="skeleton h-80" aria-hidden="true" />
        ) : byDay.length > 0 ? (
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={byDay} margin={{ top: 8, right: 4, bottom: 0, left: -12 }}>
                <defs>
                  <linearGradient id="usageRequestsGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={CHART.primary} stopOpacity={0.28} />
                    <stop offset="100%" stopColor={CHART.primary} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid vertical={false} strokeDasharray="3 3" />
                <XAxis dataKey="date" tickFormatter={shortDate} tickLine={false} axisLine={false} minTickGap={24} />
                <YAxis tickFormatter={formatCompact} tickLine={false} axisLine={false} width={48} allowDecimals={false} />
                <Tooltip content={<ChartTooltip />} labelFormatter={shortDate} cursor={{ stroke: 'rgb(var(--color-border))' }} />
                <Area type="monotone" dataKey="requests" name="Requests" stroke={CHART.primary} fill="url(#usageRequestsGradient)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <EmptyState
            icon={<BarChart3 />}
            title="No requests yet."
            description="Make your first call in the API Explorer to see usage data here."
            className="min-h-72 rounded-xl border border-dashed border-border"
          />
        )}
      </Panel>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_340px]">
        <Panel title="Usage by service" bodyClassName="px-0 pb-2">
          {usageLoading ? (
            <TableSkeleton rows={3} />
          ) : serviceRows.length > 0 ? (
            <Table minWidth={360}>
              <THead>
                <tr>
                  <Th>Service</Th>
                  <Th align="right">Requests</Th>
                  <Th align="right">Credits</Th>
                </tr>
              </THead>
              <TBody>
                {serviceRows.map((row) => (
                  <Tr key={row.service}>
                    <Td className="capitalize">{row.service}</Td>
                    <Td align="right" mono>{formatNumber(row.requests)}</Td>
                    <Td align="right" mono muted>{formatNumber(row.credits)}</Td>
                  </Tr>
                ))}
              </TBody>
            </Table>
          ) : (
            <p className="px-5 pb-4 pt-2 text-sm text-muted-foreground">No usage recorded for this period.</p>
          )}
        </Panel>

        <Panel title="Credits" action={<Link to="/billing" className="text-xs font-medium text-accent-text hover:underline">Add credits</Link>}>
          {balanceLoading ? (
            <div aria-hidden="true"><div className="skeleton h-5 w-40" /><div className="skeleton mt-3 h-5 w-36" /></div>
          ) : (
            <div className="space-y-2 text-sm">
              <p className="text-foreground">Credits remaining: {formatNumber(balance?.prepaid_balance ?? 0)}</p>
              <p className="text-muted-foreground">Used this month: {formatNumber(balance?.current_month_usage ?? 0)}</p>
              <p className="pt-1 text-xs text-muted-foreground">One credit is worth ₦0.10. Sandbox calls are free.</p>
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
};

export default UsagePage;
